/**
 * Standard assignment target picker used everywhere in the app that assigns a
 * resource to a combination of users, departments, locations, user groups,
 * sub-tenants, or "all of the tenant".
 *
 * Usage:
 *   const [targets, setTargets] = useState({
 *     userIds: [], departmentIds: [], locationIds: [], userGroupIds: [],
 *     subTenantIds: [], includeAll: false,
 *     excludes: { userIds: [], departmentIds: [], locationIds: [],
 *                 userGroupIds: [], subTenantIds: [] },
 *   });
 *   <AssignmentTargetPicker value={targets} onChange={setTargets} />
 *
 * Pair with backend AssignmentTargetResolver.ResolveAsync for expansion.
 * Final user set = (include union) − (exclude union).
 */
import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import AsyncGenericSelect from './AsyncGenericSelect';
import CheckboxInput from './CheckboxInput';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding-top: 12px;
  border-top: ${({ $withSeparator, theme }) =>
    $withSeparator ? `1px dashed ${theme?.colors?.text?.secondary || '#D0D5DD'}` : 'none'};
`;

const SectionTitle = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Row = styled.div`
  display: flex;
  flex-direction: column;
`;

export const EMPTY_EXCLUDES = Object.freeze({
  userIds: [],
  departmentIds: [],
  locationIds: [],
  userGroupIds: [],
  subTenantIds: [],
  titleIds: [],
});

export const EMPTY_TARGETS = Object.freeze({
  userIds: [],
  departmentIds: [],
  locationIds: [],
  userGroupIds: [],
  subTenantIds: [],
  titleIds: [],
  includeAll: false,
  autoApplyOnEnter: false,
  excludes: EMPTY_EXCLUDES,
});

const searchEmployees = async (term) => {
  const params = term ? { 'firstName[like]': term, pageSize: 20 } : { pageSize: 20 };
  const res = await api.get('/v1/employees', { params: { ...params, lookup: true } });
  return (res.data?.data?.data || []).map((e) => ({
    id: e.id,
    name: `${e.firstName || ''} ${e.lastName || ''}`.trim(),
    profilePicture: e.profilePicture,
  }));
};

const searchGeneric = (path, searchField = 'name[like]') => async (term) => {
  const params = term ? { [searchField]: term, pageSize: 20 } : { pageSize: 20 };
  const res = await api.get(path, { params });
  return (res.data?.data?.data || []).map((x) => ({ id: x.id, name: x.name }));
};

const searchDepartments = searchGeneric('/v1/departments');
const searchLocations = searchGeneric('/v1/locations');
const searchUserGroups = searchGeneric('/v1/user-groups');
const searchSubTenants = searchGeneric('/v1/sub-tenants');
const searchTitles = searchGeneric('/v1/titles');
const searchEmployeeStatuses = searchGeneric('/v1/employee-statuses');

/**
 * Props:
 *   value: {userIds,departmentIds,locationIds,userGroupIds,subTenantIds,includeAll,
 *           excludes: {userIds,departmentIds,locationIds,userGroupIds,subTenantIds}}
 *   onChange: (nextValue) => void
 *   disabledTypes: string[] — e.g. ['includeAll'] to hide that option
 *                  Supported keys: 'user' | 'department' | 'title' | 'location' | 'userGroup' | 'subTenant' | 'employeeStatus' | 'includeAll' | 'excludes'
 *   includeAllHint?: string — optional description under the IncludeAll checkbox
 *   showAutoApply?: boolean — "sonradan girenlere de uygula" kutusunu gösterir.
 *                  YALNIZ kayıt ÜRETEN modüllerde (politika, yan hak, yemek)
 *                  açılır; anket/duyuru/çekiliş/kapı erişiminde bu kavram yok.
 *   showEmployeeStatus?: boolean — personel statüsü (atama türü 7) seçicisini
 *                  gösterir; değer `employeeStatusIds` + `excludes.employeeStatusIds`.
 *                  VARSAYILAN KAPALI: statüyü taşımayan uçlara gönderilen seçim
 *                  sunucuda sessizce yok sayılırdı (ünvan hedefi kusurunun tekrarı).
 *                  Yalnız kaydettiği gövde AssignmentScopeRequest olan VE onu
 *                  ScopeWriteService/ScopeMatchService ile uygulayan ekranlarda açılır.
 */
export default function AssignmentTargetPicker({
  value, onChange, disabledTypes = [], includeAllHint, showAutoApply = false, showEmployeeStatus = false,
}) {
  const { t } = useTranslation();
  const v = value || EMPTY_TARGETS;
  const excludes = v.excludes || EMPTY_EXCLUDES;

  const isDisabled = useCallback((type) => disabledTypes.includes(type), [disabledTypes]);

  const patch = useCallback((partial) => onChange({ ...v, ...partial }), [onChange, v]);
  const patchExcludes = useCallback(
    (partial) => onChange({ ...v, excludes: { ...excludes, ...partial } }),
    [onChange, v, excludes],
  );

  const lists = useMemo(() => [
    { key: 'user',       field: 'userIds',       label: t('assignment.targetUser'),       excludeLabel: t('assignment.excludeUser'),       onSearch: searchEmployees },
    { key: 'department', field: 'departmentIds', label: t('assignment.targetDepartment'), excludeLabel: t('assignment.excludeDepartment'), onSearch: searchDepartments },
    { key: 'title',      field: 'titleIds',      label: t('assignment.targetTitle'),      excludeLabel: t('assignment.excludeTitle'),      onSearch: searchTitles },
    { key: 'location',   field: 'locationIds',   label: t('assignment.targetLocation'),   excludeLabel: t('assignment.excludeLocation'),   onSearch: searchLocations },
    { key: 'userGroup',  field: 'userGroupIds',  label: t('assignment.targetUserGroup'),  excludeLabel: t('assignment.excludeUserGroup'),  onSearch: searchUserGroups },
    { key: 'subTenant',  field: 'subTenantIds',  label: t('assignment.targetSubTenant'),  excludeLabel: t('assignment.excludeSubTenant'),  onSearch: searchSubTenants },
    ...(showEmployeeStatus ? [
      { key: 'employeeStatus', field: 'employeeStatusIds', label: t('assignment.targetEmployeeStatus'), excludeLabel: t('assignment.excludeEmployeeStatus'), onSearch: searchEmployeeStatuses },
    ] : []),
  ], [t, showEmployeeStatus]);

  const showExcludes = !isDisabled('excludes');
  const enabledLists = lists.filter((l) => !isDisabled(l.key));

  return (
    <Wrapper>
      {(!isDisabled('includeAll') || enabledLists.length > 0) && (
        <Section>
          <SectionTitle>{t('assignment.includedSection')}</SectionTitle>

          {!isDisabled('includeAll') && (
            <Row>
              <CheckboxInput
                label={t('assignment.includeAll')}
                checked={!!v.includeAll}
                onChange={(checked) => patch({ includeAll: checked })}
              />
              {includeAllHint ? (
                <small style={{ fontSize: 11, color: '#A0A8B4', marginTop: 4 }}>{includeAllHint}</small>
              ) : null}
            </Row>
          )}

          {showAutoApply && (
            <Row>
              <CheckboxInput
                label={t('assignment.autoApplyOnEnter')}
                checked={!!v.autoApplyOnEnter}
                onChange={(checked) => patch({ autoApplyOnEnter: checked })}
              />
              <small style={{ fontSize: 11, color: '#A0A8B4', marginTop: 4 }}>
                {t('assignment.autoApplyOnEnterHint')}
              </small>
            </Row>
          )}

          {!v.includeAll && enabledLists.map((l) => (
            <Row key={l.key}>
              <AsyncGenericSelect
                label={l.label}
                value={v[l.field] || []}
                onChange={(val) => patch({ [l.field]: val || [] })}
                onSearch={l.onSearch}
                isMulti
              />
            </Row>
          ))}
        </Section>
      )}

      {showExcludes && enabledLists.length > 0 && (
        <Section $withSeparator>
          <SectionTitle>{t('assignment.excludedSection')}</SectionTitle>
          {enabledLists.map((l) => (
            <Row key={`ex-${l.key}`}>
              <AsyncGenericSelect
                label={l.excludeLabel}
                value={excludes[l.field] || []}
                onChange={(val) => patchExcludes({ [l.field]: val || [] })}
                onSearch={l.onSearch}
                isMulti
              />
            </Row>
          ))}
        </Section>
      )}
    </Wrapper>
  );
}
